/**
 * SenderGroupsView — the "By Sender" overview. Renders the scoped folder view
 * grouped into one card per sender (sorted by most-recent message), each showing
 * the message count and inline bulk actions (archive / move / delete all). The
 * card body opens the regular list filtered to that sender.
 *
 * The two header toggles ("Skip starred" / "Skip unread") narrow BOTH the counts
 * and the per-card action sets — so "move everything from this sender that isn't
 * starred" acts on exactly what each card shows.
 */

import { useMemo, useState } from 'react';
import { format, isThisYear, isToday } from 'date-fns';
import {
  Archive,
  Trash2,
  Loader2,
  Users,
  Star,
  Mail,
  Paperclip,
  ChevronRight,
  Inbox,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { EmailSenderGroupDto, EmailFolderDto } from '@/lib/api';

type SortKey = 'date' | 'count';
type SortDir = 'asc' | 'desc';
interface SenderSort {
  key: SortKey;
  dir: SortDir;
}

// The sort is a per-device UI preference (like the swipe prefs), so it lives in
// localStorage — it survives leaving and re-opening the By Sender overview, and
// page reloads. Default matches the server order: most-recent first.
const SORT_STORAGE_KEY = 'fias.email.senderSort';
const DEFAULT_SENDER_SORT: SenderSort = { key: 'date', dir: 'desc' };

export function loadSenderSort(): SenderSort {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<SenderSort>;
      // Coerce to the known enums so a stale/garbage value can't break sorting.
      return {
        key: p.key === 'count' ? 'count' : 'date',
        dir: p.dir === 'asc' ? 'asc' : 'desc',
      };
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return DEFAULT_SENDER_SORT;
}

function saveSenderSort(sort: SenderSort): void {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // ignore unavailable storage
  }
}

function chipDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (isToday(d)) return format(d, 'h:mm a');
  return isThisYear(d) ? format(d, 'MMM d') : format(d, 'MMM d, yyyy');
}

/** A small pill toggle, matching the quick-filter chips in the message list. */
function ToggleChip({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent'
      }`}
    >
      {Icon && <Icon className="h-3 w-3" />} {label}
    </button>
  );
}

/** A sort chip: shows the field, plus a direction arrow when it's the active key. */
function SortChip({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={`Sort by ${label.toLowerCase()}${active ? (dir === 'desc' ? ' (descending)' : ' (ascending)') : ''}`}
      className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent'
      }`}
    >
      {label}
      {active &&
        (dir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
    </button>
  );
}

interface SenderGroupsViewProps {
  groups: EmailSenderGroupDto[] | undefined;
  loading: boolean;
  excludeStarred: boolean;
  excludeUnread: boolean;
  excludeAttachments: boolean;
  onToggleExcludeStarred: () => void;
  onToggleExcludeUnread: () => void;
  onToggleExcludeAttachments: () => void;
  onOpenSender: (group: EmailSenderGroupDto) => void;
  onArchive: (group: EmailSenderGroupDto) => void;
  onDelete: (group: EmailSenderGroupDto) => void;
  onMove: (group: EmailSenderGroupDto, folderId: string) => void;
  /** Folders for the per-card move menu (single-account only). */
  folders: EmailFolderDto[];
  /** Whether per-card move is offered (single-account, non-unified view). */
  canMove: boolean;
}

export function SenderGroupsView({
  groups,
  loading,
  excludeStarred,
  excludeUnread,
  excludeAttachments,
  onToggleExcludeStarred,
  onToggleExcludeUnread,
  onToggleExcludeAttachments,
  onOpenSender,
  onArchive,
  onDelete,
  onMove,
  folders,
  canMove,
}: SenderGroupsViewProps) {
  // Persisted across visits + reloads. Click a key again to flip its direction;
  // switching keys resets to descending.
  const [sort, setSort] = useState<SenderSort>(() => loadSenderSort());
  const { key: sortKey, dir: sortDir } = sort;
  const cycleSort = (key: SortKey) => {
    setSort((prev) => {
      const next: SenderSort =
        key === prev.key
          ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
          : { key, dir: 'desc' };
      saveSenderSort(next);
      return next;
    });
  };

  const sortedGroups = useMemo(() => {
    if (!groups) return groups;
    const arr = [...groups];
    arr.sort((a, b) => {
      const cmp =
        sortKey === 'date'
          ? (a.latestAt ? Date.parse(a.latestAt) : 0) - (b.latestAt ? Date.parse(b.latestAt) : 0)
          : a.count - b.count;
      // Stable tie-break by sender so equal keys keep a deterministic order.
      const tie = cmp !== 0 ? cmp : a.senderAddress.localeCompare(b.senderAddress);
      return sortDir === 'asc' ? tie : -tie;
    });
    return arr;
  }, [groups, sortKey, sortDir]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header: title + sort + the action-scope toggles. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Users className="h-4 w-4 text-muted-foreground" /> By sender
        </span>
        {groups && (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {groups.length.toLocaleString()} sender{groups.length === 1 ? '' : 's'}
          </span>
        )}
        <span className="text-[11px] text-muted-foreground">Sort:</span>
        <SortChip
          label="Date"
          active={sortKey === 'date'}
          dir={sortDir}
          onClick={() => cycleSort('date')}
        />
        <SortChip
          label="Count"
          active={sortKey === 'count'}
          dir={sortDir}
          onClick={() => cycleSort('count')}
        />
        <span className="flex-1" />
        <span className="text-[11px] text-muted-foreground">Bulk actions skip:</span>
        <ToggleChip
          active={excludeStarred}
          onClick={onToggleExcludeStarred}
          icon={Star}
          label="Starred"
        />
        <ToggleChip
          active={excludeUnread}
          onClick={onToggleExcludeUnread}
          icon={Mail}
          label="Unread"
        />
        <ToggleChip
          active={excludeAttachments}
          onClick={onToggleExcludeAttachments}
          icon={Paperclip}
          label="Attachments"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Grouping by sender…
          </div>
        ) : !sortedGroups || sortedGroups.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No messages to group here.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {sortedGroups.map((g) => {
              const name = g.senderName?.trim() || g.senderAddress;
              const initial = (name[0] ?? '?').toUpperCase();
              return (
                <div
                  key={g.senderAddress}
                  className="group flex flex-col overflow-hidden rounded-lg border bg-card transition-colors hover:border-primary/40 hover:shadow-sm"
                >
                  {/* Body — opens the sender's mail in the regular list view. */}
                  <button
                    type="button"
                    className="flex items-start gap-3 p-3 text-left"
                    title={`Show all mail from ${g.senderAddress}`}
                    onClick={() => onOpenSender(g)}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      {initial}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1">
                        <span className="truncate text-sm font-medium">{name}</span>
                        <span className="ml-auto shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-foreground">
                          {g.count.toLocaleString()}
                        </span>
                      </span>
                      {g.senderName?.trim() && (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {g.senderAddress}
                        </span>
                      )}
                      <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                        <span className="shrink-0 tabular-nums">{chipDate(g.latestAt)}</span>
                        {g.latestSubject && (
                          <>
                            <span aria-hidden>·</span>
                            <span className="truncate">{g.latestSubject}</span>
                          </>
                        )}
                      </span>
                    </span>
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>

                  {/* Bulk actions for the whole group. Wraps on narrow cards so the
                      Delete button never bleeds past the card's rounded edge. */}
                  <div className="flex flex-wrap items-center gap-1 border-t px-2 py-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                      title={`Archive all (${g.count})`}
                      onClick={() => onArchive(g)}
                    >
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </Button>
                    {canMove && (
                      <select
                        className="h-7 max-w-[7rem] rounded-md border bg-background px-1 text-xs"
                        aria-label={`Move all from ${g.senderAddress} to folder`}
                        value=""
                        onChange={(e) => {
                          const fid = e.target.value;
                          if (fid) onMove(g, fid);
                          e.currentTarget.value = '';
                        }}
                      >
                        <option value="">Move…</option>
                        {folders.map((f) => (
                          <option key={f.folderId} value={f.folderId}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-7 gap-1 px-2 text-xs text-destructive"
                      title={`Delete all (${g.count})`}
                      onClick={() => onDelete(g)}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && groups && groups.length > 0 && (
          <p className="mt-4 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
            <Inbox className="h-3 w-3" /> Click a sender to see all of their mail · bulk actions can
            be undone
          </p>
        )}
      </div>
    </div>
  );
}
